import type { CognitoUserSession } from 'amazon-cognito-identity-js';

import { AmplifyAuthWrapper } from './amplifyAuthWrapper';
import { AuthUi } from './authUi';
import { Logger } from './log';
import { Notifier } from './notifier';
import {
	initialize as initializeScheduledTokenRefresh,
	scheduleRefresh,
} from './scheduledTokenRefresh';
import { settings } from './settings';

const log = new Logger({ logger: 'authApi' });

type Args = {
	amplifyAuthWrapper: DstAuth.AmplifyWrapper | undefined;
};

/**
 * Provides access to Auth functionality
 */
class AuthApi {
	#amplify;
	#authUi;

	static StateInitialSession = 'dst.auth.initialSession';

	constructor({ amplifyAuthWrapper }: Args) {
		const { signInUri } = settings;
		log.debug('Initialize AuthApi', { signInUri });

		window.addEventListener('message', (event) => {
			if (
				(event.data as Record<string, unknown> | undefined)?.eventName ===
				Notifier.StateChangedEvent
			) {
				void this.getSession().then((session) => {
					this.onStateChanged(session);
				});
			}
		});

		this.#authUi = new AuthUi({ signInUri });
		this.#amplify = amplifyAuthWrapper ?? new AmplifyAuthWrapper();

		initializeScheduledTokenRefresh(() => this.#amplify.forceRefreshSession());

		log.debug('Get session initially');
		void this.#amplify.getSession().then((initialSession) => {
			this.#postMessage(AuthApi.StateInitialSession, initialSession);
		});
	}

	/**
	 * Returns current user session, if any. Otherwise null.
	 * @returns {Promise<CognitoUserSession>} - current user session
	 */
	async getSession() {
		// check for cognito session
		// implicitly refreshes, if expired
		log.debug('getSession triggered');
		return this.#amplify.getSession();
	}

	/**
	 * Triggers sign-in and enables the user to authenticate
	 * @param {Object} [options] - Options
	 * @param {string} [options.triggerType=pageload] - How sign-in will be triggered ("pageload", "overlay")
	 * @param {Object} [options.pageloadOptions] - PageloadOptions
	 * @param {Object} [options.pageloadOptions.target=document.location.href] - Target URI to redirect to, after successful signIn
	 * @param {Object} [options.overlayOptions] - OverlayOptions
	 * @param {Object} [options.overlayOptions.loginContext] - Indicates whether a login context should be passed to the overlay
	 * @param {Object} [options.overlayOptions.noCloseButton=false] - Indicates whether close button on overlay should be rendered
	 * @throws {AuthJsError} - on invalid parameters
	 */
	async signIn(
		options = {
			triggerType: 'pageload',
			pageloadOptions: {
				target: document.location.href,
			},
			overlayOptions: {
				colorScheme: 'light' as 'light' | 'dark',
				loginContext: undefined,
				noCloseButton: false,
			},
		},
	) {
		log.debug('signIn triggered', options);

		const session = await this.#amplify.getSession();
		if (session) {
			log.info('already signed in');
			this.onSignedIn(session);
			return;
		}

		switch (options.triggerType) {
			case 'overlay': {
				const { loginContext, noCloseButton, colorScheme } = options.overlayOptions;
				const onOverlaySuccess = () =>
					this.#amplify.getSession().then((session) => {
						this.onSignedIn(session);
					});
				this.#authUi.openSignInOverlay({
					loginContext,
					noCloseButton,
					onOverlaySuccess,
					colorScheme,
				});
				break;
			}

			case 'pageload':
			default: {
				const { target } = options.pageloadOptions;
				this.#authUi.openSignInPageload({ target });
			}
		}
	}

	/**
	 * Trigger sign-out and clears the current user session
	 * @param {Object} [options] - Options
	 * @param {boolean} [options.global=false] - Signs out users from all devices. It also invalidates all refresh tokens issued to a user.
	 */
	async signOut(options = { global: false }) {
		log.debug('signOut triggered', options);
		await this.#amplify.signOut(options);
	}

	/**
	 * Schedules a forced token refresh at the given Unix timestamp (ms).
	 *
	 * The schedule is persisted in localStorage so it survives page reloads,
	 * navigation, and browser restarts. If multiple tabs are open, all of them
	 * arm a local timer so the refresh happens even if the scheduling tab is
	 * closed; only one tab calls the Cognito API while the resulting
	 * tokenRefresh Hub event propagates updated tokens to every other tab.
	 *
	 * @param {number} timestamp Unix timestamp in milliseconds when the refresh should happen.
	 * @example
	 * // Refresh one minute from now (e.g. after a purchase that changes Cognito groups)
	 * api.scheduleTokenRefreshAt(Date.now() + 60_000);
	 */
	scheduleTokenRefreshAt(timestamp: number) {
		log.debug('scheduleTokenRefreshAt triggered', { timestamp });
		scheduleRefresh(timestamp);
	}

	/**
	 * This callback fires when a user successfully signed in
	 * @param {CognitoUserSession} session - current user session
	 */
	onSignedIn(session: CognitoUserSession | null) {
		log.debug('onSignedIn callback', session);
	}

	/**
	 * This callback fires when a user's state changes (e.g. tokenRefresh, signOut, also in another tab)
	 * @param {?CognitoUserSession} session - current user session, if any
	 */
	onStateChanged(session: CognitoUserSession | null) {
		log.debug('onStateChanged callback', session);
	}

	/**
	 * Origins can be defined in a pipe-separated string in the `TARGET_ORIGIN_ALLOWLIST` environment variable.
	 * Also supports wildcards in `TARGET_ORIGIN_ALLOWLIST` urls.
	 * @returns {string} - target origin
	 */
	#getTargetOrigin(): string | null {
		const originsArray = settings.targetOriginAllowList.split('|');
		for (const targetOrigin of originsArray) {
			// support wildcard (sub)domains in the TARGET_ORIGIN_ALLOWLIST
			if (targetOrigin.includes('*')) {
				// escape special RegExp characters (only the '.' appears in domains) and replace the wildcard '*' with the '.+' RegExp pattern
				const targetOriginRegExp = new RegExp(
					targetOrigin.replace(/[.]/g, '\\$&').replace(/\*/g, '.+'),
				);

				if (targetOriginRegExp.test(window.location.origin)) {
					return window.location.origin;
				}
			} else {
				let url;

				try {
					url = new URL(targetOrigin);
				} catch {
					throw new TypeError('Invalid URL provided as target origin');
				}

				if (url.origin !== targetOrigin) {
					throw new TypeError('Invalid origin provided as target');
				}

				if (window.location.origin === targetOrigin) {
					return targetOrigin;
				}
			}
		}

		return null;
	}

	/**
	 * Sends a message to allowed target origins.
	 * This is used to send the initial session data to subscribers and to notify about session changes.
	 * @param {string} eventName - name of the event
	 * @param {*} payload - event payload
	 */
	#postMessage(eventName: string, payload: CognitoUserSession | null) {
		const targetOrigin = this.#getTargetOrigin();

		if (!targetOrigin) {
			throw new Error('No valid target origin found');
		}

		window.postMessage(
			{
				eventName,
				payload,
			},
			targetOrigin,
		);
	}
}

export default AuthApi;
